import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CancelSaveAndContinue } from '@/core/CancelSaveAndContinue.molecule';
import { sqWorkbenchStore } from '@/core/core.stores';
import { doTrack } from '@/track/track.service';
import { setTrendGlobalScope } from '@/trendData/trend.actions';
import { useApiGetAccessControlQuery } from '@seeqdev/api/endpoint/ItemsApi';
import { Icon, Modal } from '@seeqdev/qomponents';
import type { TrackInformation } from '@/track/track.types';
import type { AceOutputV1 } from '@seeqdev/api/model/AceOutputV1';

interface ScopeModalProps {
  itemId: string;
  closeModal: () => void;
  onScopeUpdate: () => void | Promise<void>;
  workbookName?: string;
  itemType?: string;
  /** When true, the warning also says whether others can already find the item in search once it is global */
  warnAboutExistingAccess?: boolean;
}

export const ScopeModal = ({
  itemId,
  onScopeUpdate,
  closeModal,
  workbookName,
  itemType,
  warnAboutExistingAccess = false,
}: ScopeModalProps) => {
  const { t } = useTranslation();

  const [makeGlobal, setMakeGlobal] = useState(false);

  // Deliberately left to the default staleTime so permissions are re-read each time the modal opens; a cached ACL
  // could word the warning from a grant that has since been revoked.
  const { data: acl } = useApiGetAccessControlQuery({ id: itemId }, { enabled: warnAboutExistingAccess });
  const warningKey = otherAccessWarningKey(acl?.entries);

  /**
   * Makes the item available outside this analysis
   */
  const setGlobalScope = () => setTrendGlobalScope(itemId).then(onScopeUpdate).then(closeModal);

  const handleTracking = (func: () => void, info: TrackInformation) => {
    doTrack('Item Properties', 'scope dialog', info);

    return func();
  };

  const scopeDescription = workbookName
    ? t('SCOPE.MODAL.DESCRIPTION_NAMED', { analysisName: workbookName })
    : t('SCOPE.MODAL.DESCRIPTION');

  return (
    <Modal
      open={true}
      onClose={closeModal}
      testId="scopeModal"
      title={t('SCOPE.MODAL.HEADER')}
      modalFooter={
        <CancelSaveAndContinue
          submitFn={() =>
            handleTracking(setGlobalScope, itemType ? { information: 'save', itemType } : { information: 'save' })
          }
          cancelFn={() =>
            handleTracking(closeModal, itemType ? { information: 'cancel', itemType } : { information: 'cancel' })
          }
          btnDisabled={!makeGlobal}
          values={[]}
        />
      }>
      <div className="text-center">
        <div>{scopeDescription}</div>
        <div className="sq-text-warning text-bolder" data-testid="scopeWarning">
          {t(warningKey)}
        </div>
        <div
          className="mt10 mb10 font-size-larger text-interactive"
          onClick={() => setMakeGlobal((makeGlobal) => !makeGlobal)}
          data-testid="toggleScope">
          <Icon
            icon={makeGlobal ? 'fa-toggle-on' : 'fa-toggle-off'}
            iconPrefix="fa-solid"
            large={true}
            extraClassNames="pr5"
          />
          <span>{t('SCOPE.MODAL.MAKE_GLOBAL')}</span>
        </div>
      </div>
    </Modal>
  );
};

/**
 * How to word the make-global warning when others can already find the item in search once it is global.
 * Search visibility requires read, so entries without read are ignored.
 *
 * @returns the i18n key for the warning
 */
function otherAccessWarningKey(acl: AceOutputV1[] = [], userId = sqWorkbenchStore.currentUser.id): string {
  const othersWithRead = acl.filter((ace) => ace.identity?.id !== userId && ace.permissions?.read);

  if (othersWithRead.length === 0) {
    return 'SCOPE.MODAL.WARNING';
  }
  if (othersWithRead.length === 1 && othersWithRead[0].identity?.type === SeeqNames.Types.User) {
    return 'SCOPE.MODAL.WARNING_SOMEONE_ELSE';
  }
  return 'SCOPE.MODAL.WARNING_OTHER_USERS';
}
